import { useBackupController } from '@dealstack/frontend/investor/pages/Investment/common/useBackupController';
import { JOIN_ALLOCATION_CHECKS_COMPLETE_ROUTES } from '@dealstack/frontend/investor/pages/Investment/InvestmentJoinAndAllocation/JoinAndAllocationChecksComplete/JoinAndAllocationChecksComplete';
import { JOINER_ALLOCATION_PERSONAL_DETAILS_ROUTES } from '@dealstack/frontend/investor/pages/Investment/InvestmentJoinAndAllocation/JoinAndAllocationPersonalDetails/JoinAndAllocationPersonalDetails';
import { ROUTES } from '@dealstack/frontend/investor/pages/Routing/routes';
import { isStandardBackendError } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import {
	getInvestmentDetails,
	getInvestorData,
	updateInvestorCompletionStep,
} from '@dealstack/frontend/shared/dataExchange/services/shareholder/investment.service';
import { useInvestorCustomDetails } from '@dealstack/frontend/investor/pages/Investment/hooks/useInvestorCustomDetails';
import {
	useTranslationDefault,
	useTranslationInvestor,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { PrimarySideListV2Props } from '@dealstack/frontend/shared/components/list/PrimarySideList/PrimarySideListV2.types';

import { useKycWorkflowKycSetting } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useKycSetting';
import { useGetInvestorKycForms } from '@dealstack/frontend/investor/pages/Investment/hooks/useGetInvestorKycForms';
import { shouldIncludeInOtherDetails } from '@dealstack/frontend/investor/pages/Investment/InvestmentJoinAndAllocation/utils/otherDetails';
import { useWizardStepRegistry } from '@dealstack/frontend/shared/components/Wizard/WizardStepRegistry';
import type { WizardNavigationIntent } from '@dealstack/frontend/shared/components/Wizard/WizardStepRegistry.types';
import { useWrappedRequest } from '@dealstack/frontend/shared/other/react/ApiWrapper';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams';
import {
	INVESTMENT_ROUTES,
	investmentFlowPath,
} from '@dealstack/frontend/investor/pages/Investment/Investment.routes';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import {
	CustomDetailDataTypeEnum,
	DocumentWorkflowTemplateVisibleIn,
	InvestmentWorkflowCompletionStep,
	PatchShareholderInvestorCompletionStepDto,
} from '@dealstack/shared/lib/generated';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import * as Sentry from '@sentry/react';
import { useQuery } from '@tanstack/react-query';
import { isNumber } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';

export enum INVESTMENT_JOIN_ROUTES {
	PERSONAL_DETAILS = `/personal-details`,
	CHECKS_COMPLETE = `/complete-checks`,
	CONTRACTS = `/contracts`,
	COMPLETED = '/completed',
}

interface SidebarConfigItem {
	key: number;
	title: string;
	route: string;
	id?: PatchShareholderInvestorCompletionStepDto['step'];
	subitems?: (
		| {
				key: number;
				title: string;
				route: string;
				id: PatchShareholderInvestorCompletionStepDto['step'];
		  }
		| null
		| undefined
	)[];
}

type InvestmentJoinSidebarConfig = (SidebarConfigItem | null)[];

const PERSONAL_INFORMATION_STEP_KEY = 1;
const DECLARATIONS_STEP_KEY = 10;
const DOCUMENTS_STEP_KEY = 11;

interface IGetSideBarConfigOptions {
	isKycRequired?: boolean;
	isProofOfIdentityRequired?: boolean;
	isProofOfAddressRequired?: boolean;
	isProofOfInvestmentVehicleRequired?: boolean;
	anyDeclarationRequired?: boolean;
	isPIVInvestment?: boolean;
}

const getSideBarConfig = (
	t: TranslateFunction<I18Namespaces.INVESTOR>,
	options: IGetSideBarConfigOptions = {}
): InvestmentJoinSidebarConfig => {
	const {
		isKycRequired,
		isProofOfIdentityRequired = true,
		isProofOfAddressRequired = true,
		isProofOfInvestmentVehicleRequired = true,
		anyDeclarationRequired = true,
		isPIVInvestment = false,
	} = options;

	const hideScannedDocuments = !isProofOfIdentityRequired && !isProofOfAddressRequired;

	const hideCompanyIDs = !isProofOfInvestmentVehicleRequired;

	const hideInstructions = isPIVInvestment
		? !isProofOfIdentityRequired &&
			!isProofOfAddressRequired &&
			!isProofOfInvestmentVehicleRequired
		: !isProofOfIdentityRequired && !isProofOfAddressRequired;

	/**
	 * d.slaby 24.01.2023
	 * MIP-275 - when KYC is not required, hide certain sections
	 */
	return [
		{
			title: t('investment.join.sidebar.steps.personal_details'),
			route: INVESTMENT_JOIN_ROUTES.PERSONAL_DETAILS,
			key: 0,
			subitems: [
				{
					title: t('investment.basic_info'),
					route: `${INVESTMENT_JOIN_ROUTES.PERSONAL_DETAILS}${JOINER_ALLOCATION_PERSONAL_DETAILS_ROUTES.PERSONAL_INFORMATION}`,
					id: InvestmentWorkflowCompletionStep.PERSONAL_INFORMATION,
					key: PERSONAL_INFORMATION_STEP_KEY,
				},
				{
					title: t('investment.investment_vehicle'),
					route: `${INVESTMENT_JOIN_ROUTES.PERSONAL_DETAILS}${JOINER_ALLOCATION_PERSONAL_DETAILS_ROUTES.INVESTMENT_VEHICLE_INFORMATION}`,
					id: InvestmentWorkflowCompletionStep.INVESTMENT_VEHICLE_INFORMATION,
					key: 2,
				},
				{
					title: t('investment.join.sidebar.steps.bank_account'),
					route: `${INVESTMENT_JOIN_ROUTES.PERSONAL_DETAILS}${JOINER_ALLOCATION_PERSONAL_DETAILS_ROUTES.BANK_ACCOUNT}`,
					id: InvestmentWorkflowCompletionStep.BANK_ACCOUNT,
					key: 3,
				},
				{
					title: t('investment.join.sidebar.steps.others'),
					route: `${INVESTMENT_JOIN_ROUTES.PERSONAL_DETAILS}${JOINER_ALLOCATION_PERSONAL_DETAILS_ROUTES.OTHER_DETAILS}`,
					id: InvestmentWorkflowCompletionStep.OTHER,
					key: 4,
				},
			],
		},
		isKycRequired
			? {
					title: t('investment.join.sidebar.steps.complete_checks'),
					route: INVESTMENT_JOIN_ROUTES.CHECKS_COMPLETE,
					key: 5,
					subitems: [
						!hideInstructions
							? {
									title: t('investment.instructions'),
									route: `${INVESTMENT_JOIN_ROUTES.CHECKS_COMPLETE}${JOIN_ALLOCATION_CHECKS_COMPLETE_ROUTES.INSTRUCTIONS}`,
									id: InvestmentWorkflowCompletionStep.KYC_INSTRUCTIONS,
									key: 6,
								}
							: null,
						!hideScannedDocuments
							? {
									title: t('investment.personal_ids'),
									route: `${INVESTMENT_JOIN_ROUTES.CHECKS_COMPLETE}${JOIN_ALLOCATION_CHECKS_COMPLETE_ROUTES.PERSONAL}`,
									id: InvestmentWorkflowCompletionStep.KYC_PERSONAL,
									key: 7,
								}
							: null,
						!hideCompanyIDs
							? {
									title: t('investment.company_ids'),
									route: `${INVESTMENT_JOIN_ROUTES.CHECKS_COMPLETE}${JOIN_ALLOCATION_CHECKS_COMPLETE_ROUTES.COMPANY}`,
									id: InvestmentWorkflowCompletionStep.KYC_COMPANY,
									key: 8,
								}
							: null,
						{
							title: t('investment.join.sidebar.steps.forms'),
							route: `${INVESTMENT_JOIN_ROUTES.CHECKS_COMPLETE}${JOIN_ALLOCATION_CHECKS_COMPLETE_ROUTES.KYC_FORMS}`,
							id: InvestmentWorkflowCompletionStep.KYC_FORMS,
							key: 9,
						},
						anyDeclarationRequired
							? {
									title: t('investment.join.sidebar.steps.declarations'),
									route: `${INVESTMENT_JOIN_ROUTES.CHECKS_COMPLETE}${JOIN_ALLOCATION_CHECKS_COMPLETE_ROUTES.DECLARATIONS}`,
									id: InvestmentWorkflowCompletionStep.DECLARATIONS,
									key: DECLARATIONS_STEP_KEY,
								}
							: null,
					],
				}
			: null,
		{
			title: t('investment.join.sidebar.steps.contracts'),
			route: INVESTMENT_JOIN_ROUTES.CONTRACTS,
			id: InvestmentWorkflowCompletionStep.CONTRACTS,
			key: DOCUMENTS_STEP_KEY,
		},
		{
			title: t('investment.join.sidebar.steps.application_completed'),
			route: INVESTMENT_JOIN_ROUTES.COMPLETED,
			id: InvestmentWorkflowCompletionStep.COMPLETED,
			key: 12,
		},
	];
};

const SIDEBAR_MAX_ITEMS = 12;

export const useInvestmentJoinAndAllocationController = () => {
	const tDefault = useTranslationDefault();
	const tInvestor = useTranslationInvestor();
	const navigate = useNavigate();
	const { mipId, dealId, investmentWorkflowId } = useRequiredParams(
		'mipId',
		'dealId',
		'investmentWorkflowId'
	);
	const joinFlowPath = investmentFlowPath(INVESTMENT_ROUTES.JOIN_ALLOCATION, {
		dealId,
		mipId,
		investmentWorkflowId,
	});
	const { pathname } = useLocation();
	const stepRegistry = useWizardStepRegistry();

	const getInvestmentDetailsRequest = useQuery({
		queryKey: [
			API_PATHS.SHAREHOLDER_INVESTMENT_BY_MIP_ID_MIPID_GET,
			{ mipId: mipId || '', investmentWorkflowId },
		],
		queryFn: ({ signal }) =>
			getInvestmentDetails({ mipId: mipId || '', investmentWorkflowId }, signal),
		enabled: !!mipId && !!investmentWorkflowId,
	});
	const getInvestorDataRequest = useWrappedRequest(getInvestorData);
	const updateInvestorCompletionStepRequest = useWrappedRequest(updateInvestorCompletionStep, {
		disableDefaultLocalErrorHandler: true,
	});

	const [isContinueDisabled, setIsContinueDisabled] = useState(false);
	const [isBackDisabled, setIsBackDisabled] = useState(true);
	const [isLoadingState, setIsLoadingState] = useState(false);

	const investorId = getInvestorDataRequest?.response?.payload?.id;

	const investmentDetails = getInvestmentDetailsRequest.data?.payload;
	const { data: kycSetting, isLoading: isKycSettingLoading } = useKycWorkflowKycSetting(
		investmentDetails?.activeKycWorkflowId ?? ''
	);
	const getInvestorCustomDetailsRequest = useInvestorCustomDetails(
		investmentDetails?.activeKycWorkflowId ?? undefined,
		{ enabled: !!investmentDetails?.activeKycWorkflowId }
	);
	const { data: attachedKycForms } = useGetInvestorKycForms(
		investmentDetails?.activeKycWorkflowId ?? '',
		{ enabled: !!investmentDetails?.activeKycWorkflowId }
	);

	const fetchInvestorDataWithOptions = () =>
		getInvestorDataRequest.fetch({ withDraftPersonRelations: true });

	useEffect(() => {
		fetchInvestorDataWithOptions().onLocalError((error) => {
			if (isStandardBackendError(error)) {
				Sentry.captureException(
					new Error('Multiple in-progress InvestmentWorkflow associated to investor'),
					{
						extra: {
							error,
						},
					}
				);
				showToastV2({
					message: tDefault(`error_codes.${error.error?.code}`),
					state: 'error',
				});
				navigate(ROUTES.DASHBOARD);
			}
		});
	}, []);

	const isPIVInvestment = !!investmentDetails?.isPiv;

	const sideBarConfig = useMemo(() => {
		/**
		 * d.slaby 16.12.2022
		 * Without this data we can't tell what is proper sidebar configuration.
		 * Changing this will result in bugs.
		 * MIP-365
		 */
		// kycSetting alone decides whether the bank account step exists, so a config built
		// while it loads hides the step the investor is on — checkInvestorStep then reads
		// that as a settings change and resets them to the first step.
		if (!getInvestorCustomDetailsRequest?.data || !investmentDetails || isKycSettingLoading) {
			return null;
		}

		const isKycRequired = kycSetting?.isKycRequired ?? investmentDetails?.isKycRequired;
		const isProofOfIdentityRequired = kycSetting?.isProofOfIdentityRequired ?? true;
		const isProofOfAddressRequired = kycSetting?.isProofOfAddressRequired ?? true;
		const isProofOfInvestmentVehicleRequired =
			kycSetting?.isProofOfInvestmentVehicleRequired ?? true;
		const anyDeclarationRequired =
			(kycSetting?.isBeneficialOwnershipRequired ?? true) ||
			(kycSetting?.isSourceOfFundsRequired ?? true) ||
			(kycSetting?.isPepRequired ?? true);

		const config = getSideBarConfig(tInvestor, {
			isKycRequired,
			isProofOfIdentityRequired,
			isProofOfAddressRequired,
			isProofOfInvestmentVehicleRequired,
			anyDeclarationRequired,
			isPIVInvestment,
		});
		const customDetailsFields = getInvestorCustomDetailsRequest.data?.definitions;
		if (!customDetailsFields) {
			return null;
		}

		const otherCustomDetails = customDetailsFields.filter(
			(field) =>
				field.dataType !== CustomDetailDataTypeEnum.BANK_ACCOUNT &&
				field.dataType !== CustomDetailDataTypeEnum.FORM &&
				field.dataType !== CustomDetailDataTypeEnum.PRE_FILLABLE_FORM
		);
		const getHideOtherDetailsTab = () => {
			const hasRequiredPredefined =
				kycSetting?.isTaxResidenceRequired ||
				kycSetting?.isTinRequired ||
				kycSetting?.isPlaceOfBirthRequired ||
				kycSetting?.isCountryOfBirthRequired ||
				kycSetting?.isEmployeeIdRequired ||
				kycSetting?.isCitizenshipRequired;

			const hasCustomFields = otherCustomDetails.some(
				(field) =>
					field.dataType !== CustomDetailDataTypeEnum.TAX_RESIDENCY &&
					field.dataType !== CustomDetailDataTypeEnum.TIN &&
					field.dataType !== CustomDetailDataTypeEnum.COUNTRY_OF_BIRTH &&
					shouldIncludeInOtherDetails(field.type, isPIVInvestment)
			);

			return !hasRequiredPredefined && !hasCustomFields;
		};
		const hideOtherDetailsTab = getHideOtherDetailsTab();

		const getHideBankAccountTab = () => {
			return isPIVInvestment
				? !kycSetting?.isVehicleBankAccountRequired
				: !kycSetting?.isInvestorBankAccountRequired;
		};
		const hideBankAccountTab = getHideBankAccountTab();
		// The step follows the investor's CURRENT audience — a form attached in the other audience (e.g. a
		const audienceVisibleIn = isPIVInvestment
			? DocumentWorkflowTemplateVisibleIn.KYC_VEHICLE
			: DocumentWorkflowTemplateVisibleIn.KYC_INDIVIDUAL;
		const hideKycFormsTab =
			attachedKycForms !== undefined &&
			!attachedKycForms.some((form) => form.visibleIn.includes(audienceVisibleIn));

		const hidePIVKyc = !isPIVInvestment;

		const stepsToHide: (SidebarConfigItem['id'] | '')[] = [
			hideOtherDetailsTab ? InvestmentWorkflowCompletionStep.OTHER : '',
			hideBankAccountTab ? InvestmentWorkflowCompletionStep.BANK_ACCOUNT : '',
			hidePIVKyc ? InvestmentWorkflowCompletionStep.KYC_COMPANY : '',
			hideKycFormsTab ? InvestmentWorkflowCompletionStep.KYC_FORMS : '',
		];
		const purifiedConfig = config
			.filter((item) => !!item)
			.map((item) => ({ ...item, subitems: item?.subitems?.filter((item) => !!item) }));
		return purifiedConfig.map((step) => {
			if (
				!step.subitems?.find((subStep) => subStep?.id && stepsToHide.includes(subStep.id))
			) {
				return step;
			}
			return {
				...step,
				subitems: step.subitems?.filter(
					(subStep) => subStep?.id && !stepsToHide.includes(subStep.id)
				),
			};
		});
	}, [
		getInvestorCustomDetailsRequest.data,
		isPIVInvestment,
		investmentDetails?.isKycRequired,
		kycSetting,
		isKycSettingLoading,
		attachedKycForms,
	]);

	const activeMainStep = useMemo(
		() => sideBarConfig?.find((step) => step.route && pathname.includes(step.route)),
		[pathname, sideBarConfig]
	);

	const activeSubStep = useMemo(
		() =>
			activeMainStep?.subitems?.find(
				(subStep) => subStep?.route && pathname.includes(subStep.route)
			),
		[pathname, activeMainStep?.subitems]
	);

	const activeStepKey = useMemo(
		() => activeSubStep?.key || activeMainStep?.key,
		[activeMainStep, activeSubStep]
	);

	const steps = useMemo(
		() =>
			sideBarConfig?.reduce((acc, current) => {
				return [...acc, current, ...(current.subitems || [])];
			}, [] as InvestmentJoinSidebarConfig),
		[sideBarConfig]
	);

	const activeMainStepArrayIndex = useMemo(() => {
		return sideBarConfig?.findIndex((step) => {
			if (activeMainStep?.id) {
				return activeMainStep.id === step?.id;
			} else {
				return activeMainStep?.key === step?.key;
			}
		});
	}, [sideBarConfig, activeMainStep]);

	const activeSubStepArrayIndex = useMemo(() => {
		return activeMainStep?.subitems?.findIndex((subStep) => activeSubStep?.id === subStep?.id);
	}, [activeSubStep, activeMainStep]);

	const previousStep = useMemo(() => {
		if (!isNumber(activeStepKey)) {
			return;
		}

		let previousStepKey = activeStepKey - 1;
		let previousStep;

		while (previousStepKey > 0) {
			// eslint-disable-next-line no-loop-func
			const step = steps?.find((step) => step?.key === previousStepKey) as SidebarConfigItem;

			if (step && !step.subitems) {
				previousStep = step;
				break;
			}

			--previousStepKey;
		}

		return previousStep;
	}, [activeStepKey, steps]);

	useEffect(() => {
		// Personal Details is locked once the investor moves past it (needed for Docs generation).
		const previousIsPersonalDetails = !!previousStep?.route?.includes(
			INVESTMENT_JOIN_ROUTES.PERSONAL_DETAILS
		);
		const currentIsPersonalDetails = pathname.includes(INVESTMENT_JOIN_ROUTES.PERSONAL_DETAILS);
		const wouldEscapeToPersonalDetails = previousIsPersonalDetails && !currentIsPersonalDetails;

		setIsBackDisabled(
			!activeStepKey ||
				activeStepKey === PERSONAL_INFORMATION_STEP_KEY ||
				activeStepKey > DOCUMENTS_STEP_KEY ||
				wouldEscapeToPersonalDetails
		);
	}, [activeStepKey, previousStep, pathname]);

	const nextStep = useMemo(() => {
		if (!isNumber(activeStepKey)) {
			return;
		}

		let nextStepKey = activeStepKey + 1;
		let nextStep;

		while (nextStepKey <= SIDEBAR_MAX_ITEMS) {
			// eslint-disable-next-line no-loop-func
			const step = steps?.find((step) => step?.key === nextStepKey) as SidebarConfigItem;

			if (step && !step.subitems) {
				nextStep = step;
				break;
			}

			++nextStepKey;
		}

		return nextStep;
	}, [activeStepKey, steps]);

	// Read after an awaited refetch, so the route reflects the investor's current PIV status.
	const nextStepRef = useRef(nextStep);
	useEffect(() => {
		nextStepRef.current = nextStep;
	}, [nextStep]);
	// Read through a ref: the vehicle step's refetch can change the step list mid-save.
	const stepsRef = useRef(steps);
	useEffect(() => {
		stepsRef.current = steps;
	}, [steps]);

	const recordStepAndNavigate = async (route: string | undefined) => {
		const stepId = stepsRef.current?.find((step) => step?.route === route)?.id;
		if (!route || !stepId) {
			return;
		}

		await updateInvestorCompletionStepRequest.fetch({ step: stepId }).concludeWithPromise();
		await fetchInvestorDataWithOptions().concludeWithPromise();
		navigate(`${joinFlowPath}${route}`);
	};

	// D.Zghinnik 06.02.2023 MIP-451
	// Leaving vehicle information may change whether the investor is a PIV, which changes the
	// steps that follow; re-read the investor before picking the route (MIP-1625 keeps an explicit
	// return to personal information as-is).
	const resolveRouteAfterSave = async (route: string | undefined) => {
		if (activeSubStep?.id !== InvestmentWorkflowCompletionStep.INVESTMENT_VEHICLE_INFORMATION) {
			return route;
		}
		await fetchInvestorDataWithOptions().concludeWithPromise();
		return route ===
			`${INVESTMENT_JOIN_ROUTES.PERSONAL_DETAILS}${JOINER_ALLOCATION_PERSONAL_DETAILS_ROUTES.PERSONAL_INFORMATION}`
			? route
			: nextStepRef.current?.route;
	};

	const saveThenNavigate = async (intent: WizardNavigationIntent, route: string | undefined) => {
		const result = await stepRegistry.saveActiveStep(intent);
		if (!result.ok) {
			return;
		}
		await recordStepAndNavigate(await resolveRouteAfterSave(route));
	};

	useEffect(() => {
		const checkInvestorStep = () => {
			if (!steps) {
				return;
			}
			if (!getInvestorDataRequest.response || getInvestorDataRequest.isLoading) {
				return;
			}

			const currentCompletionStepId =
				getInvestorDataRequest.response?.payload?.completionStep;

			if (
				!currentCompletionStepId ||
				activeMainStep?.id === currentCompletionStepId ||
				activeSubStep?.id === currentCompletionStepId
			) {
				return;
			}

			// When the investor's join workflow is COMPLETED, additional signatures
			// (PIV / witness / representative) may still be pending. Allow them to remain
			// on /contracts so they can sign for those roles.
			if (
				currentCompletionStepId === InvestmentWorkflowCompletionStep.COMPLETED &&
				pathname.includes(INVESTMENT_JOIN_ROUTES.CONTRACTS)
			) {
				return;
			}

			const currentCompletionStepRoute = steps.find(
				(step) => step?.id === currentCompletionStepId
			)?.route;

			if (!currentCompletionStepRoute) {
				const firstStepRoute = steps.find(
					(step) => step?.id === InvestmentWorkflowCompletionStep.PERSONAL_INFORMATION
				)?.route;

				// The workflow sits on a step this programme's sidebar has no entry for, usually
				// because settings changed midway. Move the investor to the first step and let
				// their next Continue write the completion step — never write it from here, where
				// a config query that lands late would reset real progress.
				if (!firstStepRoute || pathname.includes(firstStepRoute)) {
					return;
				}

				Sentry.captureException(new Error('Investor steps mismatch'), {
					extra: {
						steps,
						currentCompletionStepId,
						getInvestorDataRequest,
						getInvestorCustomDetailsRequest,
					},
				});

				navigate(`${joinFlowPath}${firstStepRoute}`);
				return;
			}

			navigate(`${joinFlowPath}${currentCompletionStepRoute}`);
		};

		checkInvestorStep();
	}, [pathname, steps, getInvestorDataRequest.response]);

	const isLastStep = activeStepKey === SIDEBAR_MAX_ITEMS;

	const confirmsDeclarationsBeforeContracts =
		activeSubStep?.id === InvestmentWorkflowCompletionStep.DECLARATIONS &&
		nextStep?.id === InvestmentWorkflowCompletionStep.CONTRACTS;

	const onNextStep = useCallback(async () => {
		if (isLastStep) {
			if (investmentDetails) {
				const pathName = ROUTES.VALUATIONS.replace(
					':investorId',
					`${investmentDetails.investorId}`
				);
				navigate(pathName);
				return;
			}
			navigate({
				pathname: ROUTES.DASHBOARD,
				search: createSearchParams({
					mipId: mipId || '',
				}).toString(),
			});
			return;
		}

		await saveThenNavigate('next', nextStep?.route);
	}, [investmentDetails, isLastStep, activeSubStep, nextStep, activeMainStep, steps]);

	const onPreviousStep = async () => {
		if (!previousStep?.route) {
			return;
		}
		await saveThenNavigate('back', previousStep.route);
	};

	const onSidebarNavigation = async (stepKey: number) => {
		const stepData = steps?.find((step) => step?.key === stepKey);
		if (!stepData?.id) {
			return;
		}
		await saveThenNavigate('jump', stepData.route);
	};

	const showSaveDraftButton = activeMainStep?.route === INVESTMENT_JOIN_ROUTES.PERSONAL_DETAILS;
	const isLoading =
		isLoadingState ||
		getInvestorDataRequest.isLoading ||
		getInvestorCustomDetailsRequest.isLoading ||
		updateInvestorCompletionStepRequest.isLoading;

	const isInitialDataReady = !!getInvestorDataRequest.response?.payload && !!sideBarConfig;

	const { dataBackup, updateBackupData, removeBackupData } = useBackupController();

	const sideBarItems = useMemo((): PrimarySideListV2Props['sideListItems'] | undefined => {
		const isLowerThanContractsStep =
			activeMainStep?.key != null && activeMainStep?.key < DOCUMENTS_STEP_KEY;
		const onDocsStep = activeMainStep?.key === DOCUMENTS_STEP_KEY;
		// Documents depends on Personal Details, not KYC — so from Docs we let
		// the investor jump back into KYC to resolve rejections on unfinished investments
		const isKycSection = (route: string) => route === INVESTMENT_JOIN_ROUTES.CHECKS_COMPLETE;

		return sideBarConfig?.map((item) => {
			const sectionUnlocked =
				isLowerThanContractsStep || (onDocsStep && isKycSection(item.route));
			return {
				key: item.key as number,
				title: item.title as string,
				allowNavigation:
					isLowerThanContractsStep &&
					!item?.subitems?.length &&
					!!item?.id &&
					!!item?.key &&
					!!activeStepKey
						? item?.key < activeStepKey
						: false,
				allowExpandByClick:
					sectionUnlocked &&
					!!item?.subitems?.length &&
					activeMainStep?.key !== item?.key,
				subItems: item?.subitems?.map((subItem) => ({
					key: subItem?.key as number,
					title: subItem?.title as string,
					allowNavigation: sectionUnlocked && !!subItem?.key && !!activeStepKey,
				})),
			};
		});
	}, [sideBarConfig, activeStepKey, activeMainStep]);

	useEffect(() => {
		if (activeSubStep?.id === 'KYC_INSTRUCTIONS') {
			setIsContinueDisabled(false);
		}
	}, [activeStepKey, activeSubStep]);

	const redirectLink = useMemo(() => {
		return investorId
			? ROUTES.VALUATIONS.replace(':investorId', `${investorId}`)
			: ROUTES.DASHBOARD.toString();
	}, [investorId]);

	return {
		mipId,
		dealId,
		investorId,
		sideBarConfig,
		activeMainStep,
		activeSubStep,
		activeStepKey,
		onNextStep,
		nextStep,
		isLoading,
		isInitialDataReady,
		setIsLoading: setIsLoadingState,
		isContinueDisabled,
		setIsBackDisabled,
		setIsContinueDisabled,
		getInvestorDataRequest,
		getInvestorCustomDetailsRequest,
		isPIVInvestment,
		showSaveDraftButton,
		investmentDetails,
		kycSetting,
		stepRegistry,
		confirmsDeclarationsBeforeContracts,
		onPreviousStep,
		isBackDisabled,
		showContinueWarning: activeStepKey === DECLARATIONS_STEP_KEY,
		dataBackup,
		updateBackupData,
		removeBackupData,
		activeMainStepArrayIndex,
		activeSubStepArrayIndex,
		sideBarItems,
		isLastStep,
		onSidebarNavigation,
		redirectLink,
	};
};

export enum INVESTMENT_JOIN_PORTALS {
	SECTION_HEADER = '#investment-join-section-portal',
}
